import {
  AlertTriangleFilledIcon,
  IconProps,
} from '@/components/icons/AlertTriangleFilledIcon';
import { Announcement02Icon } from '@/components/icons/Announcement02Icon';
import { DataIcon } from '@/components/icons/DataIcon';
import { FileAttachment03Icon } from '@/components/icons/FileAttachment03Icon';
import { FileCheck02Icon } from '@/components/icons/FileCheck02Icon';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { Lightbulb03Icon } from '@/components/icons/Lightbulb03Icon';
import { RefreshCw04Icon } from '@/components/icons/RefreshCw04Icon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { ShowFeedbackFn } from '@/components/notifications/Feedback/feedback.context';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { NotificationKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { NotificationTrayNotificationItem } from '../components/NotificationsTray/NotificationsTab/NotificationsTab';
import {
  getAISuggestionRegenRedirectPath,
  isAiSuggestionRegenScope,
} from '../utils/aiSuggestionRegen';
import type { NotificationContainer_NotificationAppFragment } from './graphql/NotificationContainer.generated';
import {
  getValidationNotificationTarget,
  isValidationNotification,
} from './hooks/useValidationNotification';

const NOTIFICATION_ICON_MAP: Record<
  NotificationKind,
  React.ComponentType<IconProps>
> = {
  [NotificationKind.AiSuggestion]: DataIcon,
  [NotificationKind.AiSuggestionRegen]: DataIcon,
  [NotificationKind.DocumentSummary]: FileSearch02Icon,
  [NotificationKind.DocumentSummaryExported]: FileAttachment03Icon,
  [NotificationKind.FormExtractionCompleted]: FileSearch02Icon,
  [NotificationKind.PresentationGenerated]: FileAttachment03Icon,
  [NotificationKind.PresentationApprovalRequested]: FileCheck02Icon,
  [NotificationKind.ProposalGenerated]: FileAttachment03Icon,
  [NotificationKind.UnmappedIntegrationAssetClasses]: AlertTriangleFilledIcon,
  [NotificationKind.EstateInsightsGenerated]: Lightbulb03Icon,
  [NotificationKind.ManualAiDp]: RefreshCw04Icon,
  [NotificationKind.EstateSnapshotGenerated]: FileSearch02Icon,
  [NotificationKind.MemoGenerated]: FileSearch02Icon,
  [NotificationKind.MemoExported]: FileAttachment03Icon,
  // Matches the Stars01Icon on the validation callout in the app bar.
  [NotificationKind.ValidationQuestions]: Stars01Icon,
};

export interface NotificationActions {
  closeDrawer: () => void;
  markRead: (id: string) => void;
  markUnread: (id: string) => void;
  dismiss: (id: string) => void;
  navigateTo: (path: string) => void;
  trackUserEvent: ReturnType<typeof useTrackUserEvent>;
  showFeedback: ShowFeedbackFn;
  downloadDocument: (documentId: string) => Promise<void>;
  openAISuggestionsModal: (householdId: string) => void;
  openValidationQuestions: (
    householdId: string,
    conversationId: string
  ) => void;
}

function getNotificationIcon(
  kind: NotificationKind
): React.ComponentType<IconProps> {
  const mappedIcon = NOTIFICATION_ICON_MAP[kind];
  if (mappedIcon) return mappedIcon;
  diagnostics.error('Missing icon for notification kind', undefined, {
    kind,
  });
  return Announcement02Icon;
}

const DOWNLOAD_DOCUMENT_NOTIFICATION_KINDS = [
  NotificationKind.PresentationGenerated,
  NotificationKind.ProposalGenerated,
  NotificationKind.DocumentSummaryExported,
  NotificationKind.MemoExported,
];

/**
 * Maps a NotificationContainer_NotificationAppFragment to the appropriate details
 * including icon and formatting for the NotificationsTray component
 */
export function mapNotificationToTrayData(
  notification: NotificationContainer_NotificationAppFragment,
  actions: NotificationActions
): NotificationTrayNotificationItem {
  return {
    id: notification.id,
    Icon: getNotificationIcon(notification.kind),
    title: notification.title,
    description: notification.content,
    householdName: notification.household?.displayName,
    timestamp: notification.createdAt,
    variant: notification.hasPriority ? 'urgent' : 'normal',
    isUnread: !Boolean(notification.readAt),
    onClick: async () => {
      // many (though not all) notifications have a data field that contains a flag
      // indicating whether the notification is a failure.
      const isFailureNotification = notification.data?.isFailure ?? false;

      if (!notification.readAt) {
        // TODO: Once we have household details on the notification, we can
        // start generating the links
        actions.markRead(notification.id);
      }
      actions.trackUserEvent('notification clicked', {
        notificationId: notification.id,
        notificationKind: notification.kind,
      });

      try {
        // The validation opener selects the enhanced form or legacy chat.
        if (isValidationNotification(notification)) {
          const target = getValidationNotificationTarget(notification);
          if (!target) {
            throw new Error(
              'Validation notification has no household or conversation target'
            );
          }
          actions.openValidationQuestions(
            target.householdId,
            target.conversationId
          );
          return actions.closeDrawer();
        }

        // For presentation and analysis ready notifications, trigger download directly
        if (DOWNLOAD_DOCUMENT_NOTIFICATION_KINDS.includes(notification.kind)) {
          // special handling for failures
          if (isFailureNotification) {
            actions.navigateTo(
              getFailurePathForDownloadNotification(notification)
            );
            return actions.closeDrawer();
          }

          // otherwise, this should be a success; download the document directly
          const documentId = $getTargetWithKind(
            notification,
            PulidKind.Document
          );
          await actions.downloadDocument(documentId);
          actions.closeDrawer();
        } else if (notification.kind === NotificationKind.AiSuggestion) {
          const householdId = $getHouseholdId(notification);
          actions.openAISuggestionsModal(householdId); // This auto-closes the notifs tray.
        } else {
          // For other notifications, navigate to the path
          const path = getPathForNotification(
            notification,
            isFailureNotification
          );
          actions.navigateTo(path);
          actions.closeDrawer();
        }
      } catch (error) {
        actions.showFeedback(
          'This notification is missing some data and cannot be opened. Our team has been notified.'
        );
        diagnostics.error(
          'Failed to navigate to notification; missing or invalid data',
          error as Error,
          {
            notificationId: notification.id,
            notificationKind: notification.kind,
          }
        );
      }
    },
    onDelete: () => {
      actions.dismiss(notification.id);
      actions.trackUserEvent('notification dismissed', {
        notificationId: notification.id,
        notificationKind: notification.kind,
      });
    },
    onToggleRead: () => {
      if (notification.readAt) {
        actions.markUnread(notification.id);
        actions.trackUserEvent('notification marked unread', {
          notificationId: notification.id,
          notificationKind: notification.kind,
        });
      } else {
        actions.markRead(notification.id);
        actions.trackUserEvent('notification marked read', {
          notificationId: notification.id,
          notificationKind: notification.kind,
        });
      }
    },
  };
}

function $getHouseholdId(
  notification: NotificationContainer_NotificationAppFragment
): string {
  if (!notification.household) {
    throw new Error('Notification has no household');
  }
  return notification.household.id;
}

function $getTargetWithKind(
  notification: NotificationContainer_NotificationAppFragment,
  kind: PulidKind
): string {
  const targetId = notification.data?.targetIDs?.find(
    (id) => getPulidKind(id) === kind
  );
  if (!targetId) {
    throw new Error('Notification has no target with kind');
  }
  return targetId;
}

function $getTargetsWithKind(
  notification: NotificationContainer_NotificationAppFragment,
  kind: PulidKind
): string[] {
  const targetIds =
    notification.data?.targetIDs?.filter((id) => getPulidKind(id) === kind) ??
    [];
  if (targetIds.length === 0) {
    throw new Error('Notification has no target with kind');
  }
  return targetIds;
}

// AI suggestion regen notifications carry the scope on the action URL the
// backend stamped at send-time. We re-derive the in-app destination here so
// the in-app click and the email click resolve to the same place.
function getAISuggestionRegenPath(
  notification: NotificationContainer_NotificationAppFragment
): string {
  const actionURL = notification.data?.actionURL;
  if (!actionURL) {
    throw new Error('AI suggestion regen notification has no actionURL');
  }
  const parsed = new URL(actionURL);
  const scope = parsed.searchParams.get('scope');
  if (!isAiSuggestionRegenScope(scope)) {
    throw new Error(
      `AI suggestion regen notification has invalid scope: ${scope}`
    );
  }
  return getAISuggestionRegenRedirectPath({
    scope,
    householdId: $getHouseholdId(notification),
    targetId: parsed.searchParams.get('target') ?? undefined,
  });
}

export function getPathForNotification(
  notification: NotificationContainer_NotificationAppFragment,
  isFailureNotification: boolean
) {
  switch (notification.kind) {
    case NotificationKind.AiSuggestion:
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
        {
          householdId: $getHouseholdId(notification),
        }
      );
    case NotificationKind.AiSuggestionRegen:
      return getAISuggestionRegenPath(notification);
    case NotificationKind.DocumentSummary:
      // there are two cases here -- a single document or a list of documents
      // we need to handle both
      const documentIds = $getTargetsWithKind(notification, PulidKind.Document);
      if (documentIds.length === 1) {
        // if just one document, we can navigate to the single document details page
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
          {
            householdId: $getHouseholdId(notification),
            documentId: $getTargetWithKind(notification, PulidKind.Document),
          }
        );
      } else {
        // if multiple documents, we need to navigate to the documents list page
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
          {
            householdId: $getHouseholdId(notification),
          }
        );
      }
    case NotificationKind.FormExtractionCompleted: {
      // The backend stores the form submission as this notification's only target.
      const submissionId = notification.data?.targetIDs?.[0];
      if (!submissionId) {
        throw new Error(
          'Form extraction notification has no submission target'
        );
      }
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_FORM_SUBMISSION_RESULT,
        {
          householdId: $getHouseholdId(notification),
          submissionId,
        }
      );
    }
    case NotificationKind.PresentationGenerated:
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
        {
          householdId: $getHouseholdId(notification),
        },
        {
          downloadDocumentId: $getTargetWithKind(
            notification,
            PulidKind.Document
          ),
        }
      );
    case NotificationKind.ProposalGenerated:
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
        {
          householdId: $getHouseholdId(notification),
        },
        {
          downloadDocumentId: $getTargetWithKind(
            notification,
            PulidKind.Document
          ),
        }
      );
    case NotificationKind.UnmappedIntegrationAssetClasses:
      return getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_CATEGORIES, {});
    case NotificationKind.EstateInsightsGenerated:
      return getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_INSIGHTS, {
        householdId: $getHouseholdId(notification),
      });
    case NotificationKind.ManualAiDp: {
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS,
        {
          householdId: $getHouseholdId(notification),
        }
      );
    }
    case NotificationKind.EstateSnapshotGenerated: {
      if (isFailureNotification) {
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS,
          {
            householdId: $getHouseholdId(notification),
          }
        );
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2,
        {
          householdId: $getHouseholdId(notification),
          presentationId: $getTargetWithKind(
            notification,
            PulidKind.ClientPresentationV2
          ),
        }
      );
    }
    case NotificationKind.PresentationApprovalRequested:
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2,
        {
          householdId: $getHouseholdId(notification),
          presentationId: $getTargetWithKind(
            notification,
            PulidKind.ClientPresentationV2
          ),
        }
      );
    case NotificationKind.MemoGenerated: {
      if (isFailureNotification) {
        // On failure, navigate to household overview since memo may not exist
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW,
          {
            householdId: $getHouseholdId(notification),
          }
        );
      }

      // On success, navigate to the memo details page
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
        {
          householdId: $getHouseholdId(notification),
          memoId: $getTargetWithKind(notification, PulidKind.Memo),
        }
      );
    }
    case NotificationKind.DocumentSummaryExported: {
      if (isFailureNotification) {
        // On failure, navigate to the document details page (document summary details)
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
          {
            householdId: $getHouseholdId(notification),
            documentId: $getTargetWithKind(notification, PulidKind.Document),
          }
        );
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
        {
          householdId: $getHouseholdId(notification),
        },
        {
          downloadDocumentId: $getTargetWithKind(
            notification,
            PulidKind.Document
          ),
        }
      );
    }
    case NotificationKind.MemoExported: {
      if (isFailureNotification) {
        // On failure, navigate to the memo details page
        return getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
          {
            householdId: $getHouseholdId(notification),
            memoId: $getTargetWithKind(notification, PulidKind.Memo),
          }
        );
      }

      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
        {
          householdId: $getHouseholdId(notification),
        },
        {
          downloadDocumentId: $getTargetWithKind(
            notification,
            PulidKind.Document
          ),
        }
      );
    }
    case NotificationKind.ValidationQuestions:
      // Validation notifications open the household agent rather than
      // navigating; the click handler intercepts them before reaching here.
      throw new Error('Validation notifications have no navigable path');
    default:
      throw new UnreachableError({
        message: `Unreachable notification kind: ${notification.kind}`,
        case: notification.kind,
      });
  }
}

function getFailurePathForDownloadNotification(
  notification: NotificationContainer_NotificationAppFragment
): string {
  const householdId = $getHouseholdId(notification);

  switch (notification.kind) {
    case NotificationKind.PresentationGenerated:
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS,
        { householdId }
      );
    case NotificationKind.ProposalGenerated:
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_PROPOSALS,
        { householdId }
      );
    case NotificationKind.DocumentSummaryExported:
      // Navigate to the document details page (document summary details) on failure
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
        {
          householdId,
          documentId: $getTargetWithKind(notification, PulidKind.Document),
        }
      );
    case NotificationKind.MemoExported:
      // Navigate to the memo details page on failure
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
        {
          householdId,
          memoId: $getTargetWithKind(notification, PulidKind.Memo),
        }
      );
    default:
      throw new Error(`Unhandled notification kind: ${notification.kind}`);
  }
}
